import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DollarSign, Download, Search, Loader2, Users, 
  Calendar, ChevronLeft, ChevronRight,
  Eye, TrendingUp, AlertTriangle, FileText, ChevronDown, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

/**
 * AdminHostFinancials - Admin view for host financial data
 * Platform Owner only
 */
const AdminHostFinancials = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // If viewing specific host
  const { api, user, isPlatformOwner } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // For all hosts view
  const [hostsSummary, setHostsSummary] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalHosts, setTotalHosts] = useState(0);
  
  // For single host view
  const [hostData, setHostData] = useState(null);

  // Generate month options
  const monthOptions = React.useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  useEffect(() => {
    if (!isPlatformOwner?.() && !user?.is_platform_owner) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    
    if (userId) {
      fetchHostFinancials();
    } else {
      fetchAllHostsSummary();
    }
  }, [userId, selectedMonth, page]);

  const fetchAllHostsSummary = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/hosts/financials/summary?month=${selectedMonth}&page=${page}&limit=20`);
      setHostsSummary(response.data.hosts || []);
      setTotalPages(response.data.total_pages || 0);
      setTotalHosts(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching hosts summary:', error);
      toast.error('Failed to load financial summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostFinancials = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/hosts/${userId}/financials?month=${selectedMonth}`);
      setHostData(response.data);
    } catch (error) {
      console.error('Error fetching host financials:', error);
      toast.error('Failed to load host financials');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getPayoutStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case 'eligible':
        return <Badge className="bg-blue-100 text-blue-700">Ready</Badge>;
      case 'not_eligible':
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  // Header actions for month selector
  const monthSelector = (
    <select
      value={selectedMonth}
      onChange={(e) => setSelectedMonth(e.target.value)}
      className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
    >
      {monthOptions.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-slate-800">{opt.label}</option>
      ))}
    </select>
  );

  // Single Host View
  if (userId && hostData) {
    return (
      <PlatformOwnerAppShell 
        title={`${hostData.host?.full_name || 'Host'} - Financials`}
        activeId="financial"
        headerActions={monthSelector}
      >
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-xs text-white/60 mb-1">Gross Earnings</p>
                <p className="text-xl font-bold">{formatCurrency(hostData.summary?.gross_earnings)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-white/60 mb-1">Platform Fees</p>
                <p className="text-xl font-bold text-red-400">-{formatCurrency(hostData.summary?.platform_fees)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-xs text-white/60 mb-1">Processing Fees</p>
                <p className="text-xl font-bold text-red-400">-{formatCurrency(hostData.summary?.processing_fees ?? hostData.summary?.stripe_fees)}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <p className="text-xs text-emerald-400 mb-1">Net Earnings</p>
                <p className="text-xl font-bold text-emerald-300">{formatCurrency(hostData.summary?.net_earnings)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Data Quality Warning */}
          {hostData.summary?.incomplete_records > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <p className="text-sm text-amber-300">
                {hostData.summary.incomplete_records} booking(s) have incomplete fee data (legacy records).
              </p>
            </div>
          )}

          {/* Booking Details */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Booking Details ({hostData.summary?.bookings_count || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {!hostData.line_items?.length ? (
                <p className="text-center text-white/50 py-8">No bookings this month</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead>Booking</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Renter</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Platform</TableHead>
                        <TableHead className="text-right">Proc. Fee</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead>Payout</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hostData.line_items.map((item) => (
                        <TableRow key={item.booking_id}>
                          <TableCell>
                            <Link to={`/bookings/${item.booking_id}`} className="font-mono text-xs text-blue-600 hover:underline">
                              {item.booking_id?.slice(0, 12)}
                            </Link>
                            {!item.has_complete_data && (
                              <AlertTriangle className="w-3 h-3 inline ml-1 text-amber-500" title="Incomplete data" />
                            )}
                          </TableCell>
                          <TableCell className="max-w-[100px] truncate">{item.vehicle}</TableCell>
                          <TableCell>
                            <div className="max-w-[120px]">
                              <p className="truncate text-sm">{item.renter_name}</p>
                              <p className="truncate text-xs text-slate-500">{item.renter_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{item.dates}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.gross)}</TableCell>
                          <TableCell className="text-right text-red-600">-{formatCurrency(item.platform_fee)}</TableCell>
                          <TableCell className="text-right text-red-600">
                            {(item.processing_fee ?? item.stripe_fee) > 0 ? `-${formatCurrency(item.processing_fee ?? item.stripe_fee)}` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">{formatCurrency(item.net)}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {getPayoutStatusBadge(item.payout_status)}
                              {item.payout_transfer_id && (
                                <div className="flex items-center gap-1">
                                  <p className="text-xs text-slate-500 font-mono">{item.payout_transfer_id?.slice(0, 12)}...</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stored Statements */}
          {hostData.stored_statements?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Generated Statement PDFs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hostData.stored_statements.map((stmt) => (
                    <div key={stmt.month_key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{stmt.month_key}</p>
                        <p className="text-xs text-slate-500">Generated: {stmt.generated_at?.slice(0, 10)}</p>
                      </div>
                      {stmt.download_url ? (
                        <Button variant="outline" size="sm" onClick={() => window.open(stmt.download_url, '_blank')}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      ) : (
                        <Badge variant="secondary">No PDF</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </PlatformOwnerAppShell>
    );
  }

  // All Hosts Summary View
  return (
    <PlatformOwnerAppShell 
      title="Host Financials" 
      activeId="financial"
      headerActions={monthSelector}
    >
      <div className="space-y-6">
        <HostDirectModeBanner variant="subtle" context="generic" />
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : hostsSummary.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No host activity this month</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Host Earnings Summary</CardTitle>
                <CardDescription>{totalHosts} hosts with activity this month</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Platform Fees</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead className="text-center">Bookings</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hostsSummary.map((host) => (
                      <TableRow key={host.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{host.host_name}</p>
                            <p className="text-xs text-slate-500">{host.host_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(host.gross_earnings)}</TableCell>
                        <TableCell className="text-right text-red-600">-{formatCurrency(host.platform_fees)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{formatCurrency(host.net_earnings)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{host.bookings_count}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/host-financials/${host.user_id}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PlatformOwnerAppShell>
  );
};

export default AdminHostFinancials;
